/**
 * MyCreaturePanel - Shows user's creature status and spawn button
 */

import { useSimulationStore, selectMyCreature } from '../stores/simulationStore';
import { LoadingSpinner, Tooltip } from './ui';

interface MyCreaturePanelProps {
  onSpawn: () => void;
  onFollowMyCreature: () => void;
}

// Energy consumption rate per tick (approximate)
const ENERGY_DECAY_PER_TICK = 0.1;

export function MyCreaturePanel({ onSpawn, onFollowMyCreature }: MyCreaturePanelProps) {
  const displayName = useSimulationStore((state) => state.displayName);
  const myCreatureAlive = useSimulationStore((state) => state.myCreatureAlive);
  const myCreature = useSimulationStore(selectMyCreature);
  const simulationState = useSimulationStore((state) => state.simulationState);
  const actualTPS = useSimulationStore((state) => state.actualTPS);

  const energyPercent = myCreature
    ? Math.round((myCreature.energy / myCreature.maxEnergy) * 100)
    : 0;

  // Calculate estimated time until death
  const estimatedTimeLeft = myCreature && actualTPS > 0
    ? Math.round(myCreature.energy / (ENERGY_DECAY_PER_TICK * actualTPS))
    : null;

  const isLowEnergy = energyPercent <= 20;
  const isCriticalEnergy = energyPercent <= 10;

  return (
    <div className="panel my-creature-panel">
      <h3>Mi Criatura</h3>

      {displayName && (
        <div className="player-info">
          <span className="player-name">{displayName}</span>
        </div>
      )}

      {!myCreatureAlive ? (
        <div className="spawn-section">
          <p className="status-text">No tienes criatura</p>
          <button
            className="spawn-button"
            onClick={onSpawn}
            disabled={simulationState === 'stopped'}
            aria-label="Crear una nueva criatura en el mundo"
          >
            Crear Criatura
          </button>
          {simulationState === 'stopped' && (
            <p className="hint-text">Inicia la simulacion primero</p>
          )}
        </div>
      ) : myCreature ? (
        <div className="creature-status">
          <div className="status-row">
            <span className="status-label">Estado</span>
            <span className="status-value alive">Viva</span>
          </div>

          <div className="status-row">
            <Tooltip content="Energia vital. Se consume al moverse y reproducirse. Come para recuperarla.">
              <span className="status-label">Energia</span>
            </Tooltip>
            <div className={`energy-bar-container ${isCriticalEnergy ? 'critical-pulse' : ''}`}>
              <div
                className="energy-bar"
                style={{
                  width: `${energyPercent}%`,
                  backgroundColor:
                    energyPercent > 50
                      ? '#00ff88'
                      : energyPercent > 25
                      ? '#ffaa00'
                      : '#ff4444',
                }}
              />
              <span className="energy-text">{energyPercent}%</span>
            </div>
          </div>

          {isLowEnergy && (
            <div className={`energy-warning ${isCriticalEnergy ? 'critical' : ''}`}>
              <span className="warning-icon">⚠</span>
              <span className="warning-text">
                {isCriticalEnergy ? 'Energia critica!' : 'Energia baja'}
                {estimatedTimeLeft !== null && (
                  <span className="time-estimate"> (~{estimatedTimeLeft}s)</span>
                )}
              </span>
            </div>
          )}

          <div className="status-row">
            <Tooltip content="Coordenadas X,Y en el mundo. Click en el minimapa para navegar.">
              <span className="status-label">Posicion</span>
            </Tooltip>
            <span className="status-value">
              ({Math.round(myCreature.x)}, {Math.round(myCreature.y)})
            </span>
          </div>

          <button
            className="follow-button"
            onClick={onFollowMyCreature}
            aria-label="Centrar la camara en mi criatura y seguirla"
          >
            Seguir Mi Criatura
          </button>
        </div>
      ) : (
        <div className="creature-status">
          <LoadingSpinner size="small" text="Cargando..." inline />
        </div>
      )}

    </div>
  );
}
